import React, { useState } from 'react';
import { FiPlus, FiX, FiAlertCircle, FiCheckCircle, FiUser, FiTruck, FiGlobe } from 'react-icons/fi';
import { api } from '../../services/api';

const INPUT_CLASS =
  'w-full px-4 py-2.5 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all';

const VEHICLE_TYPES = [
  { key: 'fourgounette', label: 'Fourgounette' },
  { key: 'grand_fourgon', label: 'Grand Fourgon' },
  { key: 'camion_caisse', label: 'Camion Caisse' },
  { key: 'simple_cabine', label: 'Simple Cabine' },
];

const COUNTRIES = [
  'France', 'Allemagne', 'Italie', 'Espagne', 'Royaume-Uni', 'Belgique', 'Suisse',
  'Pays-Bas', 'Portugal', 'Autriche', 'Grèce', 'Pologne', 'Suède', 'Maroc',
  'Algérie', 'Tunisie', 'Égypte', 'Turquie', 'Émirats Arabes Unis', 'Arabie Saoudite',
  'États-Unis', 'Canada',
];

const INITIAL_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  identityCardNumber: '',
  vehicleType: '',
  country: '',
  markVerified: false,
};

const INITIAL_FILES = {
  drivingLicenseFront: null,
  drivingLicenseBack: null,
  vehicleRegistrationFront: null,
  vehicleRegistrationBack: null,
  identityCardFront: null,
  identityCardBack: null,
  vehiclePhoto: null,
};

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-300 mb-2">
        {label}{required ? ' *' : ''}
      </label>
      {children}
    </div>
  );
}

function FileField({ label, name, file, onChange }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-400">{label} *</label>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => onChange(name, e.target.files?.[0] || null)}
        className="block w-full text-xs text-gray-400 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-yellow-600/20 file:text-yellow-400 hover:file:bg-yellow-600/30"
      />
      {file && <p className="text-xs text-green-400 truncate">{file.name}</p>}
    </div>
  );
}

export default function CreateUserModal({ isOpen, onClose, onSuccess }) {
  const [accountType, setAccountType] = useState('client');
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [files, setFiles] = useState(INITIAL_FILES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isDemenageur = accountType.startsWith('demenageur');

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setFiles(INITIAL_FILES);
    setAccountType('client');
    setError('');
    setSuccessMessage('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setError('');
  };

  const setFile = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
    setError('');
  };

  const validate = () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      return 'Prénom et nom sont obligatoires';
    }
    if (!formData.email.trim()) return 'Email obligatoire';
    if (!formData.phone.trim()) return 'Téléphone obligatoire';
    if (!formData.password) return 'Mot de passe obligatoire';
    if (formData.password !== formData.confirmPassword) {
      return 'Les mots de passe ne correspondent pas';
    }
    if (isDemenageur) {
      if (formData.password.length < 6) {
        return 'Le mot de passe doit contenir au moins 6 caractères';
      }
      if (!formData.identityCardNumber.trim()) {
        return 'Numéro CIN obligatoire';
      }
      if (!formData.vehicleType) return 'Type de camion obligatoire';
      if (accountType === 'demenageur_international' && !formData.country) {
        return 'Pays obligatoire pour un déménageur international';
      }
      const missing = Object.entries(files).filter(([, f]) => !f).map(([k]) => k);
      if (missing.length) {
        return 'Tous les documents (permis, carte grise, CIN, photo camion) sont obligatoires';
      }
    } else {
      if (formData.password.length < 8) {
        return 'Le mot de passe client doit contenir au moins 8 caractères';
      }
      if (!/[A-Z]/.test(formData.password) || !/[a-z]/.test(formData.password) || !/\d/.test(formData.password)) {
        return 'Le mot de passe client doit contenir une majuscule, une minuscule et un chiffre';
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);

      if (isDemenageur) {
        const payload = new FormData();
        payload.append('accountType', accountType);
        payload.append('firstName', formData.first_name.trim());
        payload.append('lastName', formData.last_name.trim());
        payload.append('email', formData.email.trim());
        payload.append('phone', formData.phone.trim());
        payload.append('password', formData.password);
        payload.append('identityCardNumber', formData.identityCardNumber.trim());
        payload.append('vehicleType', formData.vehicleType);
        if (accountType === 'demenageur_international') {
          payload.append('country', formData.country);
        }
        payload.append('markVerified', formData.markVerified ? 'true' : 'false');
        Object.entries(files).forEach(([key, file]) => {
          if (file) payload.append(key, file);
        });

        const response = await api.createUser(payload);
        if (!response?.success) {
          throw new Error(response?.message || 'Erreur lors de la création');
        }
      } else {
        const response = await api.createUser({
          accountType: 'client',
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          password: formData.password,
          markVerified: true,
        });
        if (!response?.success) {
          throw new Error(response?.message || 'Erreur lors de la création');
        }
      }

      setSuccessMessage('Utilisateur créé avec succès');
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 900);
    } catch (err) {
      setError(err.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-yellow-600/30 px-6 py-5 flex items-center justify-between shrink-0">
          <h2 className="text-2xl font-bold text-yellow-400 flex items-center">
            <FiPlus className="w-6 h-6 mr-3" />
            Créer un utilisateur
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-yellow-400 transition-colors p-2 hover:bg-gray-700 rounded-lg"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
          {error && (
            <div className="p-4 bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg flex items-start">
              <FiAlertCircle className="w-5 h-5 mr-2 shrink-0 mt-0.5" />
              {error}
            </div>
          )}
          {successMessage && (
            <div className="p-4 bg-green-500/20 border border-green-500/50 text-green-400 rounded-lg flex items-center">
              <FiCheckCircle className="w-5 h-5 mr-2" />
              {successMessage}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3">Type de compte *</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { value: 'client', label: 'Client', icon: FiUser, desc: 'Utilisateur client' },
                { value: 'demenageur_local', label: 'Déménageur local', icon: FiTruck, desc: 'Transport local' },
                { value: 'demenageur_international', label: 'Déménageur international', icon: FiGlobe, desc: 'Transport à l\'étranger' },
              ].map(({ value, label, icon: Icon, desc }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAccountType(value)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    accountType === value
                      ? 'border-yellow-500 bg-yellow-500/10 text-yellow-400'
                      : 'border-gray-700 hover:border-yellow-600/40 text-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5 mb-2" />
                  <p className="font-semibold text-sm">{label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Prénom" required>
              <input
                type="text"
                required
                value={formData.first_name}
                onChange={(e) => setField('first_name', e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
            <Field label="Nom" required>
              <input
                type="text"
                required
                value={formData.last_name}
                onChange={(e) => setField('last_name', e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
          </div>

          <Field label="Email" required>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setField('email', e.target.value)}
              className={INPUT_CLASS}
              placeholder="exemple@email.com"
            />
          </Field>

          <Field label="Téléphone" required>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setField('phone', e.target.value)}
              className={INPUT_CLASS}
              placeholder="+216 XX XXX XXX"
            />
          </Field>

          {isDemenageur && (
            <>
              <div className="border-t border-gray-700 pt-5 space-y-4">
                <h3 className="text-lg font-bold text-yellow-400">Informations professionnelles</h3>

                <Field label="Numéro CIN" required>
                  <input
                    type="text"
                    value={formData.identityCardNumber}
                    onChange={(e) => setField('identityCardNumber', e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Ex : 12345678"
                  />
                </Field>

                {accountType === 'demenageur_international' && (
                  <Field label="Pays de destination" required>
                    <select
                      value={formData.country}
                      onChange={(e) => setField('country', e.target.value)}
                      className={INPUT_CLASS}
                    >
                      <option value="">Sélectionner un pays</option>
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                )}

                <Field label="Type de camion" required>
                  <select
                    value={formData.vehicleType}
                    onChange={(e) => setField('vehicleType', e.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value="">Sélectionner un type</option>
                    {VEHICLE_TYPES.map((v) => (
                      <option key={v.key} value={v.key}>{v.label}</option>
                    ))}
                  </select>
                </Field>

                <div className="rounded-xl border border-gray-700 bg-gray-900/40 p-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-300">Documents à fournir</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FileField label="Permis de conduire (recto)" name="drivingLicenseFront" file={files.drivingLicenseFront} onChange={setFile} />
                    <FileField label="Permis de conduire (verso)" name="drivingLicenseBack" file={files.drivingLicenseBack} onChange={setFile} />
                    <FileField label="Carte grise (recto)" name="vehicleRegistrationFront" file={files.vehicleRegistrationFront} onChange={setFile} />
                    <FileField label="Carte grise (verso)" name="vehicleRegistrationBack" file={files.vehicleRegistrationBack} onChange={setFile} />
                    <FileField label="CIN (recto)" name="identityCardFront" file={files.identityCardFront} onChange={setFile} />
                    <FileField label="CIN (verso)" name="identityCardBack" file={files.identityCardBack} onChange={setFile} />
                    <FileField label="Photo du camion" name="vehiclePhoto" file={files.vehiclePhoto} onChange={setFile} />
                  </div>
                  <p className="text-xs text-gray-500">JPG ou PNG, max 5 Mo par fichier</p>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.markVerified}
                    onChange={(e) => setField('markVerified', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 text-yellow-500 focus:ring-yellow-500 bg-gray-800"
                  />
                  <span className="text-sm text-gray-300">
                    Activer le compte immédiatement (vérifié et disponible)
                  </span>
                </label>
              </div>
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Mot de passe" required>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setField('password', e.target.value)}
                className={INPUT_CLASS}
                autoComplete="new-password"
              />
            </Field>
            <Field label="Confirmer le mot de passe" required>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setField('confirmPassword', e.target.value)}
                className={INPUT_CLASS}
                autoComplete="new-password"
              />
            </Field>
          </div>

          {!isDemenageur && (
            <p className="text-xs text-gray-500">
              Mot de passe client : min. 8 caractères, avec majuscule, minuscule et chiffre.
            </p>
          )}
          {isDemenageur && (
            <p className="text-xs text-gray-500">Mot de passe déménageur : min. 6 caractères.</p>
          )}

          <div className="flex space-x-3 pt-2 sticky bottom-0 bg-gradient-to-t from-gray-900 via-gray-900/95 to-transparent pb-1">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors font-semibold disabled:opacity-50"
            >
              {loading ? 'Création…' : 'Créer l\'utilisateur'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
